import { useEffect, useMemo, useRef } from "react";
// eslint-disable-next-line import/no-named-default
import { default as PassiveSection } from "src/components/shared/EntriesGenerator/Section/Section/Section";
import { type ISection } from "src/interfaces";
import { useSelector } from "src/redux/hooks";

// eslint-disable-next-line import/no-named-default
import { default as ClassicSection } from "../PortraitOrientation/PortraitSection/PortraitSection";
import styles from "./LogbookOverlay.module.scss";

interface Props {
  sectionIndex: number;
  sections?: ISection[];
}

const LogbookOverlay = ({
  sectionIndex,
  sections: overwriteSections,
}: Props) => {
  const { sections: stateSections } = useSelector(
    state => state.generatorSlice.lbentry,
  );

  const sectionRef = useRef<Element | null>(null); // Create a ref for the section element
  const overlaySectionRef = useRef<HTMLDivElement | null>(null); // Create a ref for the section element
  const wrapperRef = useRef<HTMLDivElement | null>(null); // Anchors the lookup to this section's own container

  const sections = useMemo(
    () => overwriteSections ?? stateSections,
    [overwriteSections, stateSections],
  );

  const minimizedSection: ISection | null = useMemo(
    () =>
      overwriteSections
        ? overwriteSections[sectionIndex]
        : sectionIndex !== -1 && sections?.[sectionIndex]
          ? {
              ...sections[sectionIndex],
              rows: [sections[sectionIndex].rows[0]],
            }
          : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [sections, sectionIndex],
  );

  useEffect(() => {
    if (!minimizedSection || sectionIndex === -1) return;

    const { className } = minimizedSection;
    const scope = wrapperRef.current?.parentElement ?? document;
    let sectionEl: Element | null = null;
    try {
      sectionEl = scope.querySelector(`.${className}_scroll`);
    } catch {
      sectionEl = null;
    }

    sectionRef.current = sectionEl ?? null;

    const handleHorizontalScroll = () => {
      if (sectionRef.current) {
        const elScrollX = sectionRef.current.scrollLeft;
        overlaySectionRef.current?.scrollTo({ left: elScrollX });
      }
    };

    const syncColumnWidths = () => {
      const realTable = sectionRef.current?.querySelector("table");
      const overlayTable = overlaySectionRef.current?.querySelector("table");
      if (!realTable || !overlayTable) return;

      const realRow = realTable.querySelector("tbody tr");
      const overlayRow = overlayTable.querySelector("tbody tr");
      if (!realRow || !overlayRow) return;

      const realCells = Array.from(realRow.children) as HTMLElement[];
      const overlayCells = Array.from(overlayRow.children) as HTMLElement[];

      realCells.forEach((cell, i) => {
        const target = overlayCells[i];
        if (!target) return;
        const width = `${cell.getBoundingClientRect().width}px`;
        target.style.width = width;
        target.style.minWidth = width;
        target.style.maxWidth = width;
      });

      (overlayTable as HTMLElement).style.width =
        `${realTable.getBoundingClientRect().width}px`;
    };

    handleHorizontalScroll();
    syncColumnWidths();

    sectionEl?.addEventListener("scroll", handleHorizontalScroll);

    const resizeObserver = new ResizeObserver(syncColumnWidths);
    if (sectionEl) resizeObserver.observe(sectionEl);

    return () => {
      sectionEl?.removeEventListener("scroll", handleHorizontalScroll);
      resizeObserver.disconnect();
    };
  }, [minimizedSection, sectionIndex, sectionRef, overlaySectionRef]);

  return (
    <div
      ref={wrapperRef}
      data-pdf-exclude
      style={{
        position: "sticky",
        top: -16,
        zIndex: 5,
        pointerEvents: "none",
        height: 0,
      }}>
      <div
        className={styles.logbookOverlay}
        style={{
          position: "relative",
          top: "-100%",
        }}>
        {minimizedSection && !overwriteSections && (
          <ClassicSection
            sectionIndex={sectionIndex}
            {...{ ...minimizedSection, className: "" }}
            isOverlay={true}
            ref={overlaySectionRef}
          />
        )}
        {minimizedSection && overwriteSections && (
          <PassiveSection
            section={minimizedSection}
            horizontalScroll={true}
            isOverlay={true}
            sIndex={sectionIndex}
            ref={overlaySectionRef}
          />
        )}
      </div>
    </div>
  );
};
export default LogbookOverlay;
